/**
 * UnitsList
 * - display a list of units
 * - render depending on 'installer' flag
 * - allow list to be filtered by type (residential | common area | central equipment)
 * - link list-items to detail page
 * - indicators for alert to list items
 * - sorting by column
 * - text search across all columns
 * - accept additional filters by ways of React router options
 */
import React from 'react';
import { useUnitPowerEvents } from '../use-unit-power-events';
import { unitPowerEventIds, unitPowerReadContext } from '../unit-power-read';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';

/* MRT Imports */
import { type MRT_ColumnDef } from 'material-react-table';

/* MUI */
import AddIcon from '@mui/icons-material/Add';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

/* Data */
import {
  Property,
  usePropertyBuildingsQuery,
  useUnitListByPropertyIdQuery,
  useUnitListUpdateSubscription,
} from '../../../../types/generated-types';

import {
  Fahrenheit,
  TemperatureUnit,
} from '../../../system/models/temperatureUnits';
import { FilterButtons } from '../ui/filter-buttons';

import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';
import { useAuthenticator } from '../../../auth/AuthenticationContext';
import { useAuthorizer } from '../../../auth/AuthorizationContext';
import FullWidthLoadingSkeleton from '../../shared/fullWidthLoadingSkeleton';
import {
  EnabledUnitTypes,
  MyUnit,
  UnitDataRow,
  UnitFloor,
  UnitType,
  UnitTypeName,
  unitTypes,
} from '../types/unitListTypes';

import {
  BottomMenuItems,
  useInjectableComponents,
} from '../../../system/services/injectableComponentsManager';

import { useAlertedEntitiesByProperty } from '../../../system/AlertsManager';
import { useSafePadding } from '../../../ui/helpers/safe-padding';
import { basePropertyMenuItems } from '../../properties/ui/base-property-context-menu';
import { SearchText } from '../ui/search-text';
import { UnitFilterSelector } from '../ui/unit-filter-selector';
import { LocalState } from '../../../system/services/localStateManager';
import {
  resolveInitialUnitListFilters,
  UnitsListFilterState,
} from './filterState';
import {
  bySearchTerm,
  bySelectedBuildings,
  bySelectedFloors,
  bySpecificDevices,
  byUnitType,
  columnsForUnitType as _columnsForUnitType,
  onBuildingCount,
  onFloorCount,
  onScreenSize,
  toUnitRow as _toUnitRow,
} from './lib';
import { useIsSmallScreen } from '../../../system/SystemMediaQueriesProvider';
import { MyUnitsTable } from './UnitsListTable';
import { UnitPlmIndicatorsRenderer } from '../components/UnitPlmIndicatorsRenderer';
import { usePropertyPlmContext } from '../../properties/PropertyContextWrapper';
import { clearSpecificDevicesFilter } from './unitListNavigation';

const getStorageKey = (propertyId: string): string => {
  return `unitsListFilters_${propertyId}`;
};

const loadFiltersFromStorage = (
  propertyId: string | undefined,
): UnitsListFilterState | null => {
  if (!propertyId) return null;
  return (
    LocalState.getItem<UnitsListFilterState>(getStorageKey(propertyId)) ?? null
  );
};

const saveFiltersToStorage = (
  propertyId: string | undefined,
  buildingIds: string[],
  floorIds: string[],
  sortColumnName?: string,
  sortDescending?: boolean,
  searchTerm?: string,
): void => {
  if (!propertyId) return;
  LocalState.setItem(getStorageKey(propertyId), {
    buildingIds,
    floorIds,
    sortColumnName,
    sortDescending,
    searchTerm,
  });
};

export function UnitsList({
  installerView = false,
}: {
  installerView?: boolean;
}) {
  const { id } = useParams<{ id: string }>();
  const [filterParams, setFilterParams] = useSearchParams();
  const theme = useTheme();
  const auth = useAuthenticator();
  const { can } = useAuthorizer();
  const canAccessPlm = can('be', 'super');

  // Try to get property ID from context first, fall back to route params
  const propertyContext = usePropertyPlmContext();
  const propertyId = propertyContext?.propertyId || id;
  const showPlmIndicatorsColumn =
    canAccessPlm && propertyContext?.hasScheduledEventResponses === true;

  /* specific devices are those passed in from another component. They are 
     used here to filter the number of units displayed without affecting the pathname. */
  const { pathname, state: specificDevices } = useLocation();
  const {
    setPrimaryBottomNavigationWidget,
    setContextMenuItems,
    setSubtitle,
    setSubtitlePath,
    setSubtitleActionWidget,
    setTitle,
    setTitlePath,
  } = useInjectableComponents();

  const { safeTopPadding } = useSafePadding();

  const searchParamSeparator = '_';

  const isSmall = useIsSmallScreen();

  const [selectedUnitType, setSelectedUnitType] = React.useState<
    UnitType | undefined
  >(undefined);

  /* State */
  const [property, setProperty] = React.useState<Partial<Property>>();
  const [floors, setFloors] = React.useState<UnitFloor[]>([]);
  const [showSearch, setShowSearch] = React.useState<boolean>(true);
  const [showFilter, setShowFilter] = React.useState<boolean>(!isSmall);

  /* enabled unit are those that exist in a property and can therefore be selected using the 
     filter buttons - initialize all as false and then modify according to query data */
  const [enabledUnitTypes, setEnabledUnitTypes] = React.useState<
    EnabledUnitTypes | undefined
  >(undefined);

  const [units, setUnits] = React.useState<MyUnit[]>();
  const [selectedBuildingIds, setSelectedBuildingIds] = React.useState<
    string[]
  >([]);
  const [selectedFloorIds, setSelectedFloorIds] = React.useState<string[]>([]);
  const [hasFloors, setHasFloors] = React.useState<boolean>(false);
  const [hasBuildings, setHasBuildings] = React.useState<boolean>(false);
  const [preferredUnits, setPreferredUnits] =
    React.useState<TemperatureUnit>(Fahrenheit);
  const [tableColumns, setTableColumns] = React.useState<
    MRT_ColumnDef<UnitDataRow>[]
  >([]);
  const [tableRows, setTableRows] = React.useState<UnitDataRow[]>([]);

  const [searchTerm, setSearchTerm] = React.useState<string>('');
  const [screenBreakLevel, setScreenBreakLevel] = React.useState<number>(0);
  const [sortColumnName, setSortColumnName] = React.useState('name');
  const [sortDescending, setSortDescending] = React.useState(false);

  /* --- Cached Functions --- */
  const columnsForUnitType = React.useCallback(
    (type: UnitType) =>
      _columnsForUnitType(
        type,
        installerView,
        preferredUnits,
        screenBreakLevel,
        isSmall,
        showPlmIndicatorsColumn,
      ),
    [
      installerView,
      preferredUnits,
      screenBreakLevel,
      isSmall,
      showPlmIndicatorsColumn,
    ],
  );

  const propertyAlertedEntities = useAlertedEntitiesByProperty(propertyId);

  const updateSearchFilters = React.useCallback(
    (newSearchFilters: {
      newSearchTerm?: string;
      newSelectedBuildingIds?: string[];
      newSelectedFloorIds?: string[];
      newSelectedUnitType?: UnitTypeName;
      newSortColumnName?: string;
      newSortDescending?: string;
    }) => {
      const {
        newSearchTerm,
        newSelectedBuildingIds,
        newSelectedFloorIds,
        newSelectedUnitType,
        newSortColumnName,
        newSortDescending,
      } = newSearchFilters;
      const newFilter: {
        sortBy?: string;
        sortDescending?: string;
        search?: string;
        buildings?: string;
        floors?: string;
        unitType?: string;
      } = {
        sortBy: 'name',
        sortDescending: 'false',
        search: '',
        buildings: '',
        floors: '',
        unitType: `${newSelectedUnitType}`,
      };

      if (newSortColumnName?.trim()) {
        const candidateSortColumnName = newSortColumnName?.trim();

        const newUnitType =
          unitTypes[candidateSortColumnName as UnitTypeName] ??
          selectedUnitType ??
          unitTypes.residential;

        const columnsIds = columnsForUnitType(newUnitType).map(
          (colDef) => colDef.accessorKey,
        );

        newFilter.sortBy = columnsIds.includes(candidateSortColumnName)
          ? candidateSortColumnName
          : 'name';
      } else {
        newFilter.sortBy = 'name';
      }

      if (newSortDescending?.trim()) {
        newFilter.sortDescending =
          newSortDescending.trim() === 'true' ? 'true' : 'false';
      } else {
        newFilter.sortDescending = 'false';
      }

      if (newSearchTerm) {
        if (newSearchTerm.trim().length > 0) {
          newFilter.search = newSearchTerm.trim();
        } else {
          delete newFilter.search;
        }
      } else {
        delete newFilter.search;
      }

      if (newSelectedUnitType) {
        if (newSelectedUnitType === unitTypes.residential.key) {
          delete newFilter.unitType;
        }
      } else if (selectedUnitType) {
        const oldSelectedUnitType = `${selectedUnitType.key}`;
        if (oldSelectedUnitType === unitTypes.residential.key) {
          delete newFilter.unitType;
        } else {
          newFilter.unitType = oldSelectedUnitType;
        }
      }

      if (newSelectedFloorIds) {
        if (newSelectedFloorIds.length > 0) {
          if (newSelectedFloorIds.length === property?.floors?.length) {
            delete newFilter.floors;
          } else {
            newFilter.floors = newSelectedFloorIds.join(searchParamSeparator);
          }
        } else {
          delete newFilter.floors;
        }
      } else if (selectedFloorIds.length > 0) {
        if (selectedFloorIds.length === property?.floors?.length) {
          delete newFilter.floors;
        } else {
          newFilter.floors = selectedFloorIds.join(searchParamSeparator);
        }
      } else {
        delete newFilter.floors;
      }

      if (newSelectedBuildingIds) {
        if (newSelectedBuildingIds.length > 0) {
          if (newSelectedBuildingIds.length === property?.buildings?.length) {
            // remove the 'buildings' search parameter.
            delete newFilter.buildings;
          } else {
            newFilter.buildings =
              newSelectedBuildingIds.join(searchParamSeparator);
          }
        } else {
          delete newFilter.buildings;
        }
      } else if (selectedBuildingIds.length > 0) {
        if (selectedBuildingIds.length === property?.buildings?.length) {
          // remove the 'buildings' search parameter.
          delete newFilter.buildings;
        } else {
          newFilter.buildings = selectedBuildingIds.join(searchParamSeparator);
        }
      } else {
        delete newFilter.buildings;
      }
      setFilterParams(newFilter, { state: specificDevices });

      // Determine final values for local storage (use new values if provided, otherwise current state)
      const finalBuildingIds = newSelectedBuildingIds ?? selectedBuildingIds;
      const finalFloorIds = newSelectedFloorIds ?? selectedFloorIds;
      const finalSortColumnName = newSortColumnName?.trim() ?? sortColumnName;
      const finalSortDescending =
        newSortDescending !== undefined
          ? newSortDescending.trim() === 'true'
          : sortDescending;
      const finalSearchTerm =
        newSearchTerm !== undefined ? newSearchTerm : searchTerm;

      // Save to local storage
      saveFiltersToStorage(
        propertyId,
        finalBuildingIds,
        finalFloorIds,
        finalSortColumnName,
        finalSortDescending,
        finalSearchTerm,
      );
    },
    [
      columnsForUnitType,
      property?.buildings?.length,
      property?.floors?.length,
      selectedBuildingIds,
      selectedFloorIds,
      selectedUnitType,
      setFilterParams,
      specificDevices,
      propertyId,
      sortColumnName,
      sortDescending,
      searchTerm,
    ],
  );

  // Update URL search params based on the new selected unit types
  const handleCategoryButtons = React.useCallback(
    (newSelectedType: UnitTypeName) => {
      if (selectedUnitType === undefined) return;
      if (newSelectedType !== selectedUnitType.key) {
        updateSearchFilters({ newSelectedUnitType: newSelectedType });
      }
    },
    [selectedUnitType, updateSearchFilters],
  );

  /* --- Event Handlers --- */
  const handleSearchChange = React.useCallback(
    (term: string) => {
      updateSearchFilters({ newSearchTerm: term });
      setSearchTerm(term);
    },
    [updateSearchFilters],
  );

  const handleBuildingSelect = React.useCallback(
    (buildingIds: string[]) => {
      // Reselect all applicable floors whenever the selected buildings change
      const newFloorIds = floors
        .filter((floor: UnitFloor) =>
          buildingIds.includes(floor.buildingId || ''),
        )
        .map((f) => f?._id || '');

      updateSearchFilters({
        newSelectedBuildingIds: buildingIds,
        newSelectedFloorIds: newFloorIds,
      });

      setSelectedBuildingIds(buildingIds);
      setSelectedFloorIds(newFloorIds || []);

      // updateSearchFilters will save to local storage
    },
    [floors, updateSearchFilters],
  );

  const handleFloorSelect = React.useCallback(
    (floorIds: string[]) => {
      updateSearchFilters({
        newSelectedFloorIds: floorIds,
      });
      setSelectedFloorIds(floorIds);

      // updateSearchFilters will save to local storage
    },
    [updateSearchFilters],
  );

  /* Data */
  const { data, loading, error, refetch, networkStatus } =
    useUnitListByPropertyIdQuery({
      variables: { propertyId: propertyId },
      fetchPolicy: 'network-only',
      context: unitPowerReadContext,
    });

  useUnitPowerEvents(
    refetch,
    unitPowerEventIds(
      data?.unitsByPropertyId?.flatMap(
        (unit) => unit?.separatePowerThermostatIds ?? [],
      ) ?? [],
      networkStatus,
    ),
  );

  useUnitListUpdateSubscription({
    variables: { propertyId: propertyId || '' },
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  const {
    data: buildingsData,
    loading: buildingsLoading,
    error: buildingsError,
  } = usePropertyBuildingsQuery({
    variables: { id: propertyId || '' },
    fetchPolicy: 'cache-and-network',
  });

  /* --- Effects --- */

  // Set display break point level on resize
  React.useEffect(() => {
    const handleResize = () => {
      if (isSmall) {
        setScreenBreakLevel(7);
      } else if (window.innerWidth < 670) {
        setScreenBreakLevel(6);
      } else if (window.innerWidth < 780) {
        setScreenBreakLevel(5);
      } else if (window.innerWidth < 870) {
        setScreenBreakLevel(4);
      } else if (window.innerWidth < 1000) {
        setScreenBreakLevel(3);
      } else if (window.innerWidth < 1170) {
        setScreenBreakLevel(2);
      } else if (window.innerWidth < 1220) {
        setScreenBreakLevel(1);
      } else {
        setScreenBreakLevel(0);
      }
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isSmall]);

  // Initialize preferred units
  React.useEffect(() => {
    if (auth?.user) {
      setPreferredUnits(auth.user.preferredUnits);
    }
  }, [auth]);

  /**
   * Initialize 'enabled unit types', units list according to filters,
   * floors and building settings from query results.
   * Enabled unit are those that exist in a property and can therefore
   * be selected using the filter buttons.
   **/
  React.useEffect(() => {
    /* unitTypes is a constant.  why would it ever not exist? */
    if (data?.unitsByPropertyId && unitTypes) {
      const units = data.unitsByPropertyId.filter(
        bySpecificDevices(specificDevices),
      );

      setUnits(units);

      const _enabledUnitTypes: EnabledUnitTypes = Object.values(
        unitTypes,
      ).reduce(
        (acc, unitType) => {
          acc[unitType.key] = units.some((u) => u.type === unitType.key);
          return acc;
        },
        {
          residential: false,
          commonArea: false,
          centralEquipment: false,
        } as EnabledUnitTypes,
      );
      setEnabledUnitTypes(_enabledUnitTypes);

      const uniqueIds = units.reduce(
        (
          uniqueSets: { floors: Set<string>; buildings: Set<string> },
          unit: MyUnit,
        ) => {
          if (unit.floorId && (unit.floorId?.length ?? 0) > 0) {
            uniqueSets.floors.add(unit.floorId);
          }
          if (unit.buildingId && (unit.buildingId?.length ?? 0) > 0) {
            uniqueSets.buildings.add(unit.buildingId);
          }
          return uniqueSets;
        },
        { floors: new Set<string>(), buildings: new Set<string>() },
      );

      setHasFloors(uniqueIds.floors.size > 0);
      setHasBuildings(uniqueIds.buildings.size > 0);
    }
  }, [data, specificDevices, propertyId]);

  // Initialize building and floor settings, i.e. whether there are multiple floors or buildings
  React.useEffect(() => {
    if (buildingsData?.propertyById && units !== undefined) {
      const myProperty: Partial<Property> =
        buildingsData.propertyById as Partial<Property>;

      const currentSearchTerm = filterParams.get('search')?.trim() ?? null;
      const currentBuildingIds = filterParams?.get('buildings')
        ? filterParams.get('buildings')?.split(searchParamSeparator)
        : null;
      const currentFloorIds = filterParams.get('floors')
        ? filterParams.get('floors')?.split(searchParamSeparator)
        : null;
      const currentUnitType = filterParams.get('unitType')
        ? (filterParams.get('unitType') as UnitTypeName)
        : null;
      const currentSortBy = filterParams.get('sortBy') ?? null;
      const currentSortDesc = filterParams.get('sortDescending') ?? null;

      const storedFilters = loadFiltersFromStorage(propertyId);
      const resolvedFilters = resolveInitialUnitListFilters({
        buildings: myProperty.buildings,
        floors: myProperty.floors,
        units,
        currentBuildingIds,
        currentFloorIds,
        currentUnitType,
        currentSearchTerm,
        currentSortBy,
        currentSortDesc,
        storedFilters,
      });

      setSelectedUnitType(
        resolvedFilters.unitTypeName
          ? unitTypes[resolvedFilters.unitTypeName]
          : undefined,
      );
      setSelectedBuildingIds(resolvedFilters.buildingIds);
      setSelectedFloorIds(resolvedFilters.floorIds);
      setSortColumnName(resolvedFilters.sortColumnName);
      setSortDescending(resolvedFilters.sortDescending);
      // Always set search term to match URL/local storage (even if empty)
      setSearchTerm(resolvedFilters.searchTerm);
      if (resolvedFilters.searchTerm) {
        setShowSearch(true);
      }
      if (isSmall && resolvedFilters.hasActiveLocationFilters) {
        setShowFilter(true);
      }

      // setBuildings((myProperty?.buildings as UnitBuilding[]) ?? []); -- unused state
      setFloors((myProperty?.floors as UnitFloor[]) ?? []);
      setProperty(myProperty);
    }
  }, [buildingsData, filterParams, isSmall, propertyId, units]);

  // Set up bottom navigation, title, subtitle and search & filter widgets
  React.useEffect(() => {
    if (property && pathname) {
      const searchWidget = (_showSearch: boolean) => (
        <IconButton
          sx={{
            justifyContent: 'center',
            backgroundColor: 'lightGray',
            margin: '0 4px',
            borderRadius: '4px',
            '&.MuiButtonBase-root:hover': {
              bgcolor: 'lightblue',
              '& .MuiSvgIcon-root': {
                color: 'white', // Change the color of the icon on hover
              },
            },
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowSearch(!_showSearch);
          }}
        >
          <SearchIcon fontSize="small" />
        </IconButton>
      );

      const filterWidget = (_showFilter: boolean) => (
        <IconButton
          sx={{
            justifyContent: 'center',
            backgroundColor: 'lightGray',
            margin: '0 4px',
            borderRadius: '4px',
            '&.MuiButtonBase-root:hover': {
              bgcolor: 'lightblue',
              '& .MuiSvgIcon-root': {
                color: 'white', // Change the color of the icon on hover
              },
            },
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowFilter(!_showFilter);
          }}
        >
          <FilterAltIcon fontSize="small" />
        </IconButton>
      );

      const specificDevicesWidget = (_showFilter: boolean) =>
        _showFilter && (
          <Chip
            sx={{
              mx: '4px',
              color: 'white',
              background: 'forestgreen',
              '& .MuiChip-deleteIcon': {
                color: 'white',
              },
            }}
            label={`Filtered ${!isSmall ? 'by specific devices' : ''}`}
            variant="outlined"
            onDelete={() => {
              const { params, options } =
                clearSpecificDevicesFilter(filterParams);
              setFilterParams(params, options);
            }}
          />
        );

      const items: BottomMenuItems = basePropertyMenuItems(
        property._id,
        pathname,
        [
          {
            id: 'add-unit-menu-item',
            label: 'Add Unit',
            icon: <AddIcon fontSize="small" />,
            navTarget: `/properties/${property._id}/units/add-unit`,
            permit: 'administer',
          },
        ],
      );

      const basePropertyName = property.title ?? 'Untitled Property';

      setTitle(
        pathname.includes('installer')
          ? isSmall
            ? basePropertyName.length < 19
              ? `Installing ${basePropertyName}`
              : basePropertyName.length > 24
                ? `${basePropertyName.slice(0, 24)} ...`
                : basePropertyName
            : basePropertyName
          : basePropertyName,
      );

      setTitlePath(
        pathname.includes('installer')
          ? pathname.replace('installer', 'units')
          : `/properties/${property._id}`,
      );

      setSubtitle(
        pathname.includes('installer') ? 'Installer Unit List' : 'Unit List',
      );

      setSubtitlePath(
        pathname.includes('installer')
          ? `/properties/${property._id}/units`
          : `/properties/${property._id}/summary`,
      );

      setSubtitleActionWidget({
        widget: (
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            {specificDevicesWidget(
              Array.isArray(specificDevices) && specificDevices.length > 0,
            )}
            {searchWidget(showSearch)}
            {filterWidget(showFilter)}
          </div>
        ),
      });

      setContextMenuItems(items, property as Property);

      setPrimaryBottomNavigationWidget(
        isSmall ? (
          <UnitFilterSelector
            selectedUnitType={selectedUnitType}
            enabledUnitTypes={enabledUnitTypes}
            changeHandler={handleCategoryButtons}
          />
        ) : undefined,
      );
      return () => {
        setTitle(undefined);
        setTitlePath(undefined);
        setSubtitle(undefined);
        setSubtitlePath(undefined);
        setSubtitleActionWidget(undefined);
        setContextMenuItems(undefined);
        setPrimaryBottomNavigationWidget(undefined);
      };
    }
  }, [
    enabledUnitTypes,
    filterParams,
    handleCategoryButtons,
    isSmall,
    pathname,
    property,
    selectedUnitType,
    setContextMenuItems,
    setFilterParams,
    setPrimaryBottomNavigationWidget,
    setSubtitle,
    setSubtitleActionWidget,
    setSubtitlePath,
    setTitle,
    setTitlePath,
    updateSearchFilters,
    showFilter,
    showSearch,
    specificDevices,
    theme.palette.primary.main,
    propertyId,
  ]);

  React.useEffect(() => {
    if (selectedUnitType) {
      /**
       * Select columns to display:
       * Filter out 'Buildings' and 'Floors' columns on small devices, or if a property doesn't have multiple of either.
       */
      const columns = columnsForUnitType(selectedUnitType)
        .filter(onScreenSize(isSmall))
        .filter(onFloorCount(hasFloors))
        .filter(onBuildingCount(hasBuildings));

      setTableColumns(columns);
    }
  }, [selectedUnitType, isSmall, hasFloors, hasBuildings, columnsForUnitType]);

  // Set up rows and columns of the DataGrid
  React.useEffect(() => {
    if (units && selectedUnitType) {
      /**
       * Select rows to display:
       * Leave the search-filter for last, so that the columns are set and
       * there are fewer records to search and sort
       */

      const myRows = selectedUnitType
        ? units
            .filter(byUnitType(selectedUnitType))
            .filter(bySelectedBuildings(selectedBuildingIds))
            .filter(bySelectedFloors(selectedFloorIds))
            .map(
              _toUnitRow(
                selectedUnitType,
                propertyAlertedEntities,
                preferredUnits,
                installerView,
              ),
            )
            .filter(bySearchTerm(searchTerm))
        : [];

      setTableRows(myRows);
    }
  }, [
    units,
    selectedUnitType,
    selectedBuildingIds,
    selectedFloorIds,
    searchTerm,
    propertyAlertedEntities,
    preferredUnits,
    installerView,
  ]);

  /* --- Data Grid Utility Functions --- */

  /* UI */
  const err = error || buildingsError;
  const isLoading = loading || buildingsLoading;

  return err ? (
    err.message === 'Unauthorized' ? (
      <>
        <Typography variant="h4">
          You are not authorized to access the requested resource.
        </Typography>
        <Typography variant="body1">
          Please contact your administrator or Embue support for assistance.
        </Typography>
      </>
    ) : (
      <>
        <Typography variant="h4">
          Error accessing the requested resource.
        </Typography>
        <Typography variant="body1">
          Please contact your administrator or Embue support for assistance.
        </Typography>
      </>
    )
  ) : isLoading ? (
    <FullWidthLoadingSkeleton />
  ) : (
    <Box
      sx={{
        display: 'flex',
        // maxWidth: isSmall
        //   ? '100vw'
        //   : screenBreakLevel > 2
        //     ? 'calc(100vw - 85px)'
        //     : 'calc(100vw - 65px)',
        flexDirection: 'column',
        flex: 1,
        alignItems: 'stretch',
        cursor: 'pointer',
        overflowY: 'auto',
      }}
    >
      {showFilter && (
        <FilterButtons
          theme={theme}
          category={unitTypes}
          enabledCategories={enabledUnitTypes}
          selectedCategory={selectedUnitType}
          buildings={buildingsData?.propertyById}
          selectedBuildingIds={selectedBuildingIds}
          selectedFloorIds={selectedFloorIds}
          handleSelect={handleCategoryButtons}
          handleBuildingSelect={handleBuildingSelect}
          handleFloorSelect={handleFloorSelect}
        />
      )}
      {showSearch && (
        <SearchText
          handleChange={handleSearchChange}
          padding={isSmall ? '6px' : '2vw'}
          initialValue={searchTerm}
        />
      )}
      {selectedUnitType ? (
        tableColumns && tableRows ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              flex: '1 1 auto',
              padding: isSmall ? '6px' : '10px',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                width: '100%',
                height: '100%',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: '1 1 auto',
                  maxWidth: isSmall ? '0px' : undefined,
                }}
              ></div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: '0 1 auto',
                  width: '100%',
                  // isSmall
                  //   ? 'calc(100vw - 4px)'
                  //   : screenBreakLevel > 4
                  //     ? '82vw'
                  //     : screenBreakLevel > 3
                  //       ? '84vw'
                  //       : screenBreakLevel > 2
                  //         ? '86vw'
                  //         : '88vw',
                  height: 'auto',
                }}
              >
                {buildingsData ? (
                  <>
                    <MyUnitsTable
                      tableColumns={tableColumns}
                      tableRows={tableRows}
                      updateSearchFilters={updateSearchFilters}
                      screenBreakLevel={screenBreakLevel}
                      isSmall={isSmall}
                      safeTopPadding={safeTopPadding}
                      showSearch={showSearch}
                      showFilter={showFilter}
                      buildingsData={buildingsData}
                      sortColumnName={sortColumnName}
                      sortDescending={sortDescending}
                    />
                    {showPlmIndicatorsColumn && (
                      <UnitPlmIndicatorsRenderer
                        propertyId={propertyId || ''}
                        unitType={selectedUnitType?.key}
                        searchTerm={searchTerm}
                        sortColumnName={sortColumnName}
                        sortDescending={sortDescending}
                      />
                    )}
                  </>
                ) : null}
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: '1 1 auto',
                }}
              ></div>
            </div>
          </div>
        ) : (
          <div>Initializing ...</div>
        )
      ) : enabledUnitTypes ? (
        <div>No Units found.</div>
      ) : (
        <div>Initializing...</div>
      )}
    </Box>
  );
}
